// @owners { team: patients-team }
import { get } from '~shared/helpers/apiHelper';
import { type NextAvailableDateResponse } from '~shared/types/clients';

export type FetchNextAvailableDatesV3Params = {
  addressID?: number;
  facilityID?: number;
  unbundledPrescriptionIDs?: number[];
  bundledPrescriptionIDs?: number[];
  shipmentID?: number | null;
};

const buildQueryParams = (params: FetchNextAvailableDatesV3Params): Record<string, string | number> => {
  const { addressID, facilityID, unbundledPrescriptionIDs = [], bundledPrescriptionIDs = [], shipmentID } = params;

  const query: Record<string, string | number> = {};

  if (typeof addressID === 'number') {
    query.address_id = addressID;
  } else if (typeof facilityID === 'number') {
    query.facility_id = facilityID;
  }

  if (unbundledPrescriptionIDs.length > 0) {
    query.unbundled = unbundledPrescriptionIDs.join(',');
  }

  if (bundledPrescriptionIDs.length > 0) {
    query.bundled = bundledPrescriptionIDs.join(',');
  }

  if (typeof shipmentID === 'number') {
    query.shipment_id = shipmentID;
  }

  return query;
};

/** Fetches next available dates from `GET /v3/next_available_dates` (same query params as the Redux thunk). */
export async function fetchNextAvailableDatesV3(
  params: FetchNextAvailableDatesV3Params,
): Promise<NextAvailableDateResponse[]> {
  const { addressID, facilityID } = params;
  if (typeof addressID !== 'number' && typeof facilityID !== 'number') {
    throw new Error('One of addressID or facilityID is required.');
  }

  const unbundled = params.unbundledPrescriptionIDs ?? [];
  const bundled = params.bundledPrescriptionIDs ?? [];
  const { shipmentID } = params;

  if (unbundled.length === 0 && bundled.length === 0 && typeof shipmentID !== 'number') {
    return [];
  }

  const json = await get('/next_available_dates', buildQueryParams(params), { version: 'v3' });

  if (json && typeof json === 'object' && 'error' in json && json.error) {
    throw json.error;
  }

  return json as NextAvailableDateResponse[];
}
